import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ArrowLeft, AlertTriangle, Clock, Users, Zap, Shield, CheckCircle2,
  XCircle, ChevronRight, FileText, Activity, Server, GitBranch, AlertCircle
} from 'lucide-react';
import PageHeader from '../shared/PageHeader';
import StatusBadge from '../shared/StatusBadge';
import ScorePill from '../shared/ScorePill';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const RISK_COLORS = {
  LOW: 'bg-[hsl(var(--status-ok))]/15 text-[hsl(var(--status-ok))] border-[hsl(var(--status-ok))]/25',
  MEDIUM: 'bg-[hsl(var(--status-warn))]/15 text-[hsl(var(--status-warn))] border-[hsl(var(--status-warn))]/25',
  HIGH: 'bg-[hsl(var(--status-crit))]/15 text-[hsl(var(--status-crit))] border-[hsl(var(--status-crit))]/25'
};

const IncidentDetail = () => {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [incRes, sumRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/appsupport/incidents/${incidentId}`),
        fetch(`${BACKEND_URL}/api/appsupport/incidents/${incidentId}/summary`)
      ]);
      if (incRes.ok) setIncident(await incRes.json());
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [incidentId]);

  const executeAction = async (actionRef) => {
    setExecuting(actionRef);
    try {
      const res = await fetch(`${BACKEND_URL}/api/appsupport/incidents/${incidentId}/execute-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_ref: actionRef, confirmation: true })
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(`Action ${actionRef} executed successfully`);
      } else {
        toast.error(d.detail || 'Action failed');
      }
    } catch (err) {
      toast.error('Failed to execute action');
    } finally {
      setExecuting(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-background" data-testid="incident-detail-loading">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-12 gap-4">
          <Skeleton className="col-span-8 h-96" />
          <Skeleton className="col-span-4 h-96" />
        </div>
      </div>
    </div>
  );

  if (!incident) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Card className="p-8 text-center">
        <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
        <p className="text-foreground font-medium">Incident not found</p>
        <Button onClick={() => navigate('/appsupport/dashboard')} className="mt-4" variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
        </Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="incident-detail">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/appsupport/dashboard')} data-testid="incident-detail-back-button">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1
                className="text-xl md:text-2xl font-semibold tracking-tight text-foreground"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {incident.incident_id}
              </h1>
              <ScorePill score={incident.business_impact_score} type="bis" />
              <StatusBadge status={incident.status} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {incident.workflow_name} - {incident.failure_step}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Main Content */}
          <div className="col-span-12 xl:col-span-8 space-y-4">
            {/* Impact Summary */}
            <Card data-testid="incident-detail-impact-card">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-crit))]" />
                  Impact Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <div className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{incident.business_impact_score}</div>
                    <div className="text-xs text-muted-foreground">Business Impact</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <div className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{incident.affected_record_count}</div>
                    <div className="text-xs text-muted-foreground">Affected Records</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <div className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{incident.sla_exposure_minutes}m</div>
                    <div className="text-xs text-muted-foreground">SLA Exposure</div>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/50">
                    <div className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>{incident.confidence_score}%</div>
                    <div className="text-xs text-muted-foreground">AI Confidence</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Root Cause Hypothesis */}
            <Card data-testid="incident-detail-root-cause-card">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Zap className="h-4 w-4 text-[hsl(var(--status-warn))]" />
                  Root Cause Hypothesis
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-sm text-foreground leading-relaxed">{incident.root_cause_hypothesis}</p>
              </CardContent>
            </Card>

            {/* Evidence Chain */}
            <Card data-testid="incident-detail-evidence-card">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[hsl(var(--status-info))]" />
                  Evidence Chain
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-3">
                  {incident.evidence_chain?.map((evidence, idx) => (
                    <div key={idx} className="relative pl-8">
                      {idx < incident.evidence_chain.length - 1 && (
                        <div className="absolute left-[14px] top-7 bottom-0 w-px bg-border" />
                      )}
                      <div className={`absolute left-0 top-1 flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold
                        ${evidence.relevance === 'HIGH' ? 'bg-[hsl(var(--status-crit))]/15 text-[hsl(var(--status-crit))]' : 'bg-[hsl(var(--status-warn))]/15 text-[hsl(var(--status-warn))]'}
                      `}>
                        {idx + 1}
                      </div>
                      <div className="p-3 rounded-lg border border-border">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs font-mono">{evidence.source}</Badge>
                          <Badge variant={evidence.relevance === 'HIGH' ? 'default' : 'secondary'} className="text-xs">{evidence.relevance}</Badge>
                        </div>
                        <p className="text-sm text-foreground">{evidence.finding}</p>
                        <p className="text-xs text-muted-foreground font-mono mt-1">{evidence.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Context Cards */}
            <div className="grid grid-cols-2 gap-4">
              {incident.deployment_context && (
                <Card data-testid="incident-detail-deployment-context-card">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <GitBranch className="h-4 w-4" /> Deployment Context
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      {incident.deployment_context.did_recent_deploy_precede_failure ?
                        <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-warn))]" /> :
                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-ok))]" />
                      }
                      <span className="text-muted-foreground">
                        {incident.deployment_context.did_recent_deploy_precede_failure ?
                          `Recent deploy: ${incident.deployment_context.application_name} v${incident.deployment_context.version} (${incident.deployment_context.deployed_minutes_ago}m ago)` :
                          'No recent deployment detected'
                        }
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
              {incident.infra_context && (
                <Card data-testid="incident-detail-infra-context-card">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Server className="h-4 w-4" /> Infrastructure Context
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      {incident.infra_context.infrastructure_anomaly_detected ?
                        <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-warn))]" /> :
                        <CheckCircle2 className="h-4 w-4 text-[hsl(var(--status-ok))]" />
                      }
                      <span className="text-muted-foreground">
                        {incident.infra_context.infrastructure_anomaly_detected ?
                          incident.infra_context.description :
                          'No infrastructure anomalies detected'
                        }
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Sidebar: Remediation Actions */}
          <div className="col-span-12 xl:col-span-4 space-y-4">
            <Card data-testid="incident-detail-remediation-card">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[hsl(var(--ring))]" />
                  Suggested Remediation
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                {incident.suggested_remediation?.map((action, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-border space-y-2" data-testid="remediation-action-card">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{action.action}</span>
                      <Badge variant="outline" className={`text-xs ${RISK_COLORS[action.risk_level] || ''}`}>
                        {action.risk_level}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-mono">{action.runbook_ref}</span>
                      {action.pre_approved && (
                        <Badge className="text-xs bg-[hsl(var(--status-ok))]/15 text-[hsl(var(--status-ok))] border-[hsl(var(--status-ok))]/25" variant="outline">
                          Pre-approved
                        </Badge>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={action.pre_approved ? 'default' : 'outline'}
                      className="w-full h-8 text-xs"
                      onClick={() => executeAction(action.runbook_ref)}
                      disabled={executing === action.runbook_ref}
                      data-testid="remediation-action-execute-button"
                    >
                      {executing === action.runbook_ref ? 'Executing...' : action.pre_approved ? 'Execute Now' : 'Request Approval'}
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Affected Applications */}
            <Card data-testid="incident-detail-affected-apps-card">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Affected Applications
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  {incident.affected_applications?.map((appId) => (
                    <div key={appId} className="flex items-center gap-2 text-sm py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--status-warn))]"></span>
                      <span className="font-mono text-muted-foreground">{appId}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetail;
